import React, { useEffect, useState } from 'react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { Layout as AntLayout, Skeleton } from 'antd'
//
import global from 'common'

import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import { WizardDto } from 'core/application/adminSignUp/common/models'
import { StepStatus, WizardStep } from 'core/domain/wizard/wizard'

import Modal from 'common/components/feedback/Modal'

import useLoginUser from 'common/hooks/useLoginUser'

import NotFound from 'errorPages/NotFound'

import { LOGIN_PATH } from '../../account/login/Login'
import useQueryRequest from '../../common/hooks/useQueryRequest'
import Layout from '../../common/layout/Layout'
import { ActiveItemProps, DEFAULT_CATEGORY_INDEX, DEFAULT_CATEGORY_ITEM_INDEX } from '../SignUpNavigationMenu'
import SignUpNavigationMenu from '../SignUpNavigationMenu'
import { getActiveItem, getNavigationItemFullPath } from '../common/helpers'
import { ADMIN_SIGNUP_PATH, RoutesMapping } from '../index'

import '../SignupStyles.less'

const { Sider, Content } = AntLayout

interface AdminSignupProps {
  getWizard: () => Promise<WizardDto>
  getWizardState: () => Promise<any>
}

const AdminSignUpWizard: React.FC<AdminSignupProps> = (props) => {
  const { isLg } = global.hooks.useBreakPoints()
  const location = useLocation()
  const history = useHistory()

  const [user] = useLoginUser()

  const [activeItem, setActiveItem] = useState<ActiveItemProps>()
  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [adminSignupState, setAdminSignupState] = useState<any | undefined>()
  const [wizard] = useQueryRequest<WizardDto>(null, () => props.getWizard())
  const [, , sendGetWizardStateRequest] = useQueryRequest<any>(null)

  useEffect(() => {
    if (!userIsAllowedToEnterAdminSignUpFlow(user)) history.push(LOGIN_PATH)
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!wizard || !wizard.steps || wizard.steps.length == 0) return

      const activeItem = getActiveItem(location.pathname, wizard.steps, user.productId)
      setActiveItem(activeItem)

      const state = await sendGetWizardStateRequest(() => props.getWizardState())
      setAdminSignupState(state)

      setHasDirtyData(false)
    })()
  }, [location, wizard])

  const userIsAllowedToEnterAdminSignUpFlow = (user: User): boolean => {
    return user !== null && user.permissions.includes(Permissions.AccessAdminSignUpWizard)
  }

  const redirectTo = (path: string) => {
    if (path !== location.pathname)
      if (hasDirtyData) {
        Modal.confirm({
          title: 'Are you sure you want to leave the page?',
          content: 'You may lose changes you have made to this page.',
          onOk() {},
          onCancel() {
            history.push(path)
          },
          okText: 'Stay',
          okType: 'primary',
          cancelText: 'Discard',
        })
      } else {
        history.push(path)
      }
  }

  let showDesktopSideMenu = isLg

  return (
    <Skeleton
      active
      loading={!wizard}
      paragraph={{
        rows: 10,
      }}
      className="skeleton"
    >
      {wizard && wizard.steps && (
        <Layout>
          <AntLayout className="sign_up_content bg-gray-2">
            {showDesktopSideMenu ? (
              <Sider width={240}>
                <SignUpNavigationMenu state={wizard.steps} activeItem={activeItem} redirectTo={redirectTo} />
              </Sider>
            ) : (
              <Sider className="ee_sidebar_navigation" width={35}>
                <SignUpNavigationMenu isMobile state={wizard.steps} activeItem={activeItem} redirectTo={redirectTo} />
              </Sider>
            )}

            <Content className="content">
              <Skeleton active loading={adminSignupState === undefined}>
                {wizard.steps.length > 0 && adminSignupState !== undefined && (
                  <DynamicWizardStepBasedOnRoute
                    wizard={wizard}
                    wizardState={adminSignupState}
                    hasDirtyData={setHasDirtyData}
                  />
                )}
              </Skeleton>
            </Content>
          </AntLayout>
        </Layout>
      )}
    </Skeleton>
  )
}

export default AdminSignUpWizard

type DynamicWizardStepBasedOnRouteProps = {
  wizard: WizardDto
  wizardState: any
  hasDirtyData: (dirtyData: boolean) => void
}

const DynamicWizardStepBasedOnRoute: React.FC<DynamicWizardStepBasedOnRouteProps> = (props) => {
  const [user] = useLoginUser()
  const history = useHistory()

  const getPreviousItem = (currentItem: string): WizardStep => {
    try {
      const currentCategoryIndex = props.wizard.steps.findIndex((cat) =>
        cat.children.some((item) => item.id === currentItem),
      )
      if (currentCategoryIndex === -1) throw new Error('Category not found')
      const currentCategory = props.wizard.steps[currentCategoryIndex]
      const itemIndex = currentCategory.children.findIndex((item) => item.id === currentItem)
      if (itemIndex === -1) throw new Error('Category item not found')
      if (itemIndex > 0) {
        return currentCategory.children[itemIndex - 1]
      } else if (currentCategoryIndex > 0) {
        const previousCategory = props.wizard.steps[currentCategoryIndex - 1]
        if (previousCategory === undefined) throw new Error('Previous Category not found')
        return previousCategory.children[previousCategory.children.length - 1]
      }
      return props.wizard.steps[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    } catch (e) {
      console.log(e)
      return props.wizard.steps[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    }
  }

  const getNextItem = (currentItem: string): WizardStep => {
    try {
      const currentCategoryIndex = props.wizard.steps.findIndex((cat) =>
        cat.children.some((item) => item.id === currentItem),
      )

      if (currentCategoryIndex === -1) throw new Error('Category not found')
      const currentCategory = props.wizard.steps[currentCategoryIndex]

      const itemIndex = currentCategory.children.findIndex((item) => item.id === currentItem)
      if (itemIndex === -1) throw new Error('Category item not found')

      const item = currentCategory.children[itemIndex]
      item.status = StepStatus.Complete

      if (itemIndex < currentCategory.children.length - 1) {
        let nextItem = currentCategory.children[itemIndex + 1]
        if (nextItem.status === StepStatus.Disabled) {
          nextItem.status = StepStatus.Active
        }
        return nextItem
      } else if (currentCategoryIndex < props.wizard.steps.length - 1) {
        const nextCategory = props.wizard.steps[currentCategoryIndex + 1]
        if (nextCategory === undefined) throw new Error('Next Category not found')

        const nextItem = nextCategory.children[0]

        if (nextItem.status === StepStatus.Disabled) {
          nextItem.status = StepStatus.Active
        }

        return nextItem
      }
      return props.wizard.steps[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    } catch (e) {
      return props.wizard.steps[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    }
  }

  const goBack = (from: string) => {
    const prevItem = getPreviousItem(from)
    const prevItemPath = getNavigationItemFullPath(prevItem, user.productId)
    if (props.hasDirtyData) {
      Modal.confirm({
        title: 'Are you sure you want to leave the page?',
        content: 'You may lose changes you have made to this page.',
        onOk() {},
        onCancel() {
          history.push(prevItemPath)
        },
        okText: 'Stay',
        okType: 'primary',
        cancelText: 'Discard',
      })
    } else {
      history.push(prevItemPath)
    }
  }

  const goNext = (from: string) => {
    const nextItem = getNextItem(from)
    const nextItemPath = getNavigationItemFullPath(nextItem, user.productId)
    history.push(nextItemPath)
  }

  return (
    <Switch>
      {RoutesMapping.get(user.productId).map((item: any) => (
        <Route
          key={item.key}
          path={ADMIN_SIGNUP_PATH + item.route}
          render={(renderProps) => (
            <item.component
              name={item.key}
              goBack={goBack}
              goNext={goNext}
              hasDirtyData={props.hasDirtyData}
              state={item.mapState(props.wizardState)}
              {...renderProps}
            />
          )}
        />
      ))}

      <Route>
        <NotFound />
      </Route>
    </Switch>
  )
}
